import {
  CustomerCardSchema,
  CustomerPageSchema,
  type AddressInput,
  type CompanyInput,
  type ContactInput,
  type CreateCustomerRequest,
  type CustomerCardDto,
  type UpdateCustomerRequest,
} from '@deli/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getJson, sendJson } from './client.js';

export interface CustomerFilter {
  search?: string;
  type?: 'b2c' | 'b2b';
  status?: 'pending_approval' | 'active' | 'rejected' | 'blocked';
  offset?: number;
}

export const CUSTOMER_PAGE = 50;

export const useCustomers = (filter: CustomerFilter) =>
  useQuery({
    queryKey: ['customers', 'list', filter],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: String(CUSTOMER_PAGE) });
      if (filter.search) params.set('search', filter.search);
      if (filter.type) params.set('type', filter.type);
      if (filter.status) params.set('status', filter.status);
      if (filter.offset) params.set('offset', String(filter.offset));
      return getJson(`/api/v1/customers?${params}`, CustomerPageSchema, { signal });
    },
    placeholderData: keepPreviousData,
  });

/** Opening the card is logged by the API as personal-data access, so it is fetched once. */
export const useCustomerCard = (id: string) =>
  useQuery({
    queryKey: ['customers', 'card', id],
    queryFn: ({ signal }) => getJson(`/api/v1/customers/${id}`, CustomerCardSchema, { signal }),
    staleTime: Infinity,
  });

export function useCreateCustomer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCustomerRequest) =>
      sendJson('POST', '/api/v1/customers', body, CustomerCardSchema),
    onSuccess: (card) => {
      client.setQueryData(['customers', 'card', card.id], card);
      void client.invalidateQueries({ queryKey: ['customers', 'list'] });
    },
  });
}

type Method = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Every change on the card answers with the whole updated card, which replaces the cached one —
 * no re-fetch, so the audit log records only real views.
 */
export function useCardChange<T>(id: string, request: (input: T) => [Method, string, unknown?]) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: T) => {
      const [method, path, body] = request(input);
      return sendJson(method, `/api/v1/customers/${id}${path}`, body, CustomerCardSchema);
    },
    onSuccess: (card: CustomerCardDto) => {
      client.setQueryData(['customers', 'card', id], card);
      void client.invalidateQueries({ queryKey: ['customers', 'list'] });
    },
  });
}

export const useUpdateCustomer = (id: string) =>
  useCardChange(id, (body: UpdateCustomerRequest) => ['PATCH', '', body]);

export const useChangeStatus = (id: string) =>
  useCardChange(id, (body: { status: CustomerFilter['status'] & string; reason: string }) => [
    'PUT',
    '/status',
    body,
  ]);

export const useSaveCompany = (id: string) =>
  useCardChange(id, (body: CompanyInput) => ['PUT', '/company', body]);

export const useRemoveCompany = (id: string) =>
  useCardChange(id, (_: void) => ['DELETE', '/company']);

export const useAddContact = (id: string) =>
  useCardChange(id, (body: ContactInput) => ['POST', '/contacts', body]);

export const useRemoveContact = (id: string) =>
  useCardChange(id, (contactId: string) => ['DELETE', `/contacts/${contactId}`]);

export const useAddAddress = (id: string) =>
  useCardChange(id, (body: AddressInput) => ['POST', '/addresses', body]);

export const useUpdateAddress = (id: string) =>
  useCardChange(id, ({ addressId, ...body }: AddressInput & { addressId: string }) => [
    'PUT',
    `/addresses/${addressId}`,
    body,
  ]);

export const useRemoveAddress = (id: string) =>
  useCardChange(id, (addressId: string) => ['DELETE', `/addresses/${addressId}`]);
